// The web's DataClient for the shared Backtest / Paper panes (desktop
// docs/005 §5a): the data lives on the user's desktop, so each read is a relay
// data request answered by that desktop (POST /api/relay/rpc, then poll).
import { API_BASE_URL } from '../../config';

function authHeaders() {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function rpc(method, params) {
  const r = await fetch(`${API_BASE_URL}/api/relay/rpc`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ method, params }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `request failed (${r.status})`);
  const started = Date.now();
  // The desktop answers within a second or two; the relay gives up at 30 s.
  while (Date.now() - started < 32000) {
    await new Promise((res) => setTimeout(res, 600));
    const p = await fetch(`${API_BASE_URL}/api/relay/rpc/${d.request_id}`, { headers: authHeaders() });
    const s = await p.json().catch(() => ({}));
    if (s.status === 'done') return s.result;
    if (s.status === 'error') throw new Error(s.error || 'the desktop app could not answer');
  }
  throw new Error("The desktop app didn't answer. Is it open?");
}

// Alert email recipients (Stock-Screener doc 288) live on prod: the web calls prod's REST API
// directly with the web session, so this works even when the desktop is off.
async function prodJson(method, path, body) {
  const r = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = r.status === 204 ? {} : await r.json().catch(() => ({}));
  return { status: r.status, ok: r.ok, data: d };
}

async function mutate(method, path, body) {
  const { ok, status, data } = await prodJson(method, path, body);
  if (!ok) throw new Error(data.error || `request failed (${status})`);
}

const recipientsPath = (alertId) => `/api/alerts/${encodeURIComponent(alertId)}/recipients`;

export const recipientsApi = {
  async list(alertId) {
    const { ok, status, data } = await prodJson('GET', recipientsPath(alertId));
    if (status === 404) return { synced: false, recipients: [] }; // not synced to prod yet
    if (!ok) throw new Error(data.error || `request failed (${status})`);
    return { synced: true, ...data };
  },
  add: (alertId, email) => mutate('POST', recipientsPath(alertId), { email }),
  remove: (alertId, recipientId) => mutate('DELETE', `${recipientsPath(alertId)}/${recipientId}`),
  resend: (alertId, recipientId) => mutate('POST', `${recipientsPath(alertId)}/${recipientId}/resend`, {}),
};

export const relayDataClient = {
  backtestRun: (runId) => rpc('backtest_run_detail', { run_id: runId }),
  deployment: (deploymentId) => rpc('deployment_detail', { deployment_id: deploymentId }),
  setDeployment: (deploymentId, action) => rpc('deployment_set', { deployment_id: deploymentId, action }).then(() => {}),
  // No push channel from the desktop: refresh an open paper account once a minute.
  onDeploymentUpdate(deploymentId, cb) {
    const t = setInterval(cb, 60000);
    return () => clearInterval(t);
  },
  alertsOverview: () => rpc('alerts_overview', {}),
  alert: (alertId) => rpc('alert_detail', { alert_id: alertId }),
  setAlert: (alertId, action) => rpc('alert_set', { alert_id: alertId, action }).then(() => {}),
  watchlist: (watchlistId) => rpc('watchlist_detail', { watchlist_id: watchlistId }),
  // No push channel: refresh alerts once a minute while the pane is open.
  onAlertsUpdate(cb) {
    const t = setInterval(cb, 60000);
    return () => clearInterval(t);
  },
  recipients: recipientsApi,
  capabilities: { controlDeployments: true, localPaths: false, controlAlerts: true },
};
